export const US_COUNTRY_ID = "231";

export const TIER3_RESTRICTED_US_STATES = [
  "Arkansas",
  "Connecticut",
  "District of Columbia",
  "Illinois",
  "Kansas",
  "Massachusetts",
  "Michigan",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "North Carolina",
  "Wisconsin",
];

export const TIER3_RESTRICTED_MESSAGE =
  "Tier 3 job posts are not yet available in this state. Tier 1 and Tier 2 are available.";

const TIER3_RESTRICTED_LOOKUP = new Set(
  TIER3_RESTRICTED_US_STATES.map((stateName) => normalizeStateName(stateName)),
);

export function normalizeStateName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
}

export function isTier3RestrictedState(stateName) {
  if (!stateName) return false;
  return TIER3_RESTRICTED_LOOKUP.has(normalizeStateName(stateName));
}

export function findStateInLocation(location, states = []) {
  if (!location || !Array.isArray(states) || states.length === 0) {
    return null;
  }

  const normalizedLocation = normalizeStateName(location);
  const sortedStates = [...states].sort(
    (a, b) => (b?.name || "").length - (a?.name || "").length,
  );

  return (
    sortedStates.find((state) => {
      const stateName = state?.name || "";
      return (
        stateName &&
        normalizedLocation.includes(normalizeStateName(stateName))
      );
    }) || null
  );
}

export function detectUsStateIdFromLocation(location, states = []) {
  const matchedState = findStateInLocation(location, states);
  return matchedState ? String(matchedState.id) : "";
}

export function findRestrictedStateInLocation(location) {
  if (!location) return null;

  const normalizedLocation = normalizeStateName(location);
  const sortedStates = [...TIER3_RESTRICTED_US_STATES].sort(
    (a, b) => b.length - a.length,
  );

  return (
    sortedStates.find((stateName) =>
      normalizedLocation.includes(normalizeStateName(stateName)),
    ) || null
  );
}

export function stripUsStateFromLocation(location, states = []) {
  if (!location) return "";

  let result = location.trim();

  states.forEach((state) => {
    const stateName = state?.name;
    if (!stateName) return;

    const escapedName = stateName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result
      .replace(new RegExp(`,\\s*${escapedName}\\s*,?\\s*(United States|USA)?`, "i"), "")
      .replace(new RegExp(`,\\s*${escapedName}\\s*$`, "i"), "");
  });

  return result
    .replace(/,\s*(United States|USA)\s*$/i, "")
    .trim()
    .replace(/,\s*$/, "");
}

export function buildLocationWithUsState(cityPart, stateName) {
  const city = String(cityPart || "")
    .trim()
    .replace(/,\s*$/, "");

  if (!stateName) return city;
  if (!city) return stateName;
  return `${city}, ${stateName}`;
}

export function getSelectedUsStateName(stateId, states = []) {
  if (!stateId) return "";
  const matchedState = states.find(
    (state) => String(state.id) === String(stateId),
  );
  return matchedState?.name || "";
}

export function isTier3BlockedForUsJob(countryId, stateName, jobTier) {
  return (
    String(countryId) === US_COUNTRY_ID &&
    parseInt(jobTier, 10) === 3 &&
    isTier3RestrictedState(stateName)
  );
}

export function getTier3UsJobValidationError(jobState) {
  if (!jobState || String(jobState.country) !== US_COUNTRY_ID) {
    return null;
  }

  if (parseInt(jobState.job_tier, 10) !== 3) {
    return null;
  }

  if (findRestrictedStateInLocation(jobState.location)) {
    return TIER3_RESTRICTED_MESSAGE;
  }

  return null;
}

export function getUsStateRequiredValidationError(jobState, states = []) {
  if (!jobState || String(jobState.country) !== US_COUNTRY_ID) {
    return null;
  }

  const matchedState =
    states.length > 0
      ? findStateInLocation(jobState.location, states)
      : findStateNameInLocationStatic(jobState.location);

  if (!matchedState) {
    return "Please select a US state for this job location.";
  }

  return null;
}

const US_STATE_NAMES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export function findStateNameInLocationStatic(location) {
  if (!location) return null;

  const normalizedLocation = normalizeStateName(location);
  const sortedStates = [...US_STATE_NAMES].sort((a, b) => b.length - a.length);

  return (
    sortedStates.find((stateName) =>
      normalizedLocation.includes(normalizeStateName(stateName)),
    ) || null
  );
}

export function buildUsStateSelectOptions(states = []) {
  const options = { 0: "Select US State" };
  states.forEach((state) => {
    options[String(state.id)] = state.name;
  });
  return options;
}
